import { useTranslation } from 'react-i18next';
import { Exhibition } from '@/shared/types';
import { ContentLanguageCode } from '@/shared/constants';
import { getExhibitionColorInfo } from '@/features/exhibitions/utils/exhibitionColors';
import draftIcon from '@/assets/svg/draft_icon.svg';
import pencilIcon from '@/assets/svg/pencil_icon.svg';
import checkIconCircle from '@/assets/svg/check_icon_circle.svg';

interface ExhibitionReadViewProps {
    exhibition: Exhibition;
    currentLanguage: ContentLanguageCode;
    imageUrl: string | null;
    onEdit: () => void;
}

export const ExhibitionReadView: React.FC<ExhibitionReadViewProps> = ({
    exhibition,
    currentLanguage,
    imageUrl,
    onEdit,
}) => {
    const { t } = useTranslation();

    const title = exhibition.name?.[currentLanguage] || '';
    const description = exhibition.description?.[currentLanguage] || '';

    // Récupérer le statut de la langue courante (ou draft par défaut)
    const status = exhibition.status?.[currentLanguage] || 'draft';
    const colorInfo = getExhibitionColorInfo(exhibition.color || 'default');

    // Formater les dates (gérer les Timestamp Firestore)
    const formatDate = (date: Date | { toDate?: () => Date; seconds?: number; nanoseconds?: number } | string | number | undefined) => {
        if (!date) return '';
        let d: Date;
        if (date instanceof Date) {
            d = date;
        } else if (typeof date === 'object' && date !== null && 'toDate' in date && typeof date.toDate === 'function') {
            // Timestamp Firestore
            d = date.toDate();
        } else if (typeof date === 'object' && date !== null && 'seconds' in date && typeof date.seconds === 'number') {
            // Timestamp Firestore (format { seconds, nanoseconds })
            d = new Date(date.seconds * 1000);
        } else if (typeof date === 'string' || typeof date === 'number') {
            d = new Date(date);
        } else {
            return '';
        }
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Récupérer les dates de la langue courante (ou de la première langue disponible)
    const createdAt = exhibition.createdAt
        ? formatDate(exhibition.createdAt[currentLanguage] || Object.values(exhibition.createdAt)[0])
        : '';
    const updatedAt = exhibition.updatedAt
        ? formatDate(exhibition.updatedAt[currentLanguage] || Object.values(exhibition.updatedAt)[0])
        : '';

    return (
        <div className="exhibition-read-view">
            <div className="exhibition-read-card">
                <div className="read-content">
                    <div className="read-image">
                        {imageUrl ? (
                            <img src={imageUrl} alt={title} />
                        ) : (
                            <div className="image-placeholder">{t('exhibitions.imagePlaceholder')}</div>
                        )}
                    </div>

                    <div className="read-text">
                        <div className="read-header">
                            <div className="read-metadata">
                                {status === 'published' && (
                                    <div className="read-badge published">
                                        <img src={checkIconCircle} alt="" className="badge-icon" />
                                        <span>{t('exhibitions.published')}</span>
                                    </div>
                                )}
                                {status === 'draft' && (
                                    <div className="read-badge draft">
                                        <img src={draftIcon} alt="" className="badge-icon" />
                                        <span>{t('exhibitions.draft')}</span>
                                    </div>
                                )}
                                {(createdAt || updatedAt) && (
                                    <span className="read-dates">
                                        {updatedAt && `Mis à jour le ${updatedAt}`}
                                        {updatedAt && createdAt && ' · '}
                                        {createdAt && `Créé le ${createdAt}`}
                                    </span>
                                )}
                                <div className="read-color-badge" style={{ backgroundColor: colorInfo.hex }}>
                                    <span className="read-color-badge-icon" style={{ backgroundColor: colorInfo.hex }}></span>
                                    <span>{colorInfo.name}</span>
                                </div>
                            </div>
                            <button className="edit-button" onClick={onEdit} aria-label={t('common.edit')}>
                                <img src={pencilIcon} alt="" className="edit-icon" />
                            </button>
                        </div>
                        <h1 className="read-title">{title}</h1>
                        <div className="read-description">
                            {description.split('\n').map((line, index) => (
                                <p key={index}>{line}</p>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

